import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../../redux/instance/api";

/**
 * Fetch member appointments with date range filtering
 */
export const getMemberAppointments = createAsyncThunk(
  "member/appointments/fetch",
  async ({ startDate, endDate, isRecentOnly = false } = {}, { rejectWithValue }) => {
    try {
      const limit = isRecentOnly ? 10 : 100;
      const requestBody = {};

      if (startDate) requestBody.startDate = startDate;
      if (endDate) requestBody.endDate = endDate;

      const response = await api.post(
        `/appointment/search?offset=0&limit=${limit}&orderBy=id&direction=ASC`,
        requestBody
      );

      if (response.data?.response_message === "Success") {
        return {
          status: "success",
          message: response.data,
        };
      } else {
        return rejectWithValue(
          response.data?.message?.payload || "Failed to fetch appointments"
        );
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message?.payload || "Failed to fetch appointments"
      );
    }
  }
);


export const cancelAppointment = createAsyncThunk(
  "member/appointments/cancel",
  async ({ appointmentId }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/appointment/cancel/${appointmentId}`,
        {}
      );

      if (response.data?.response_message === "Success") {
        return {
          status: "success",
          message: response.data,
          appointmentId,
        };
      } else {
        return rejectWithValue(
          response.data?.message?.payload || "Failed to cancel appointment"
        );
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message?.payload || "Failed to cancel appointment"
      );
    }
  }
);

/**
 * Fetch available time slots for rescheduling
 */
export const getAvailableSlots = createAsyncThunk(
  "member/appointments/availableSlots",
  async ({ counsellorId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/counsellor/${counsellorId}/available-slot?startDate=${startDate}&endDate=${endDate}`
      );

      if (response.data?.response_message === "Success") {
        return {
          status: "success",
          message: response.data,
        };
      } else {
        return rejectWithValue(
          response.data?.message?.payload || "Failed to fetch available slots"
        );
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message?.payload || "Failed to fetch available slots"
      );
    }
  }
);

/**
 * Reschedule appointment by updating existing appointment
 * Uses PATCH to update existing appointment instead of creating new one
 */
export const rescheduleAppointment = createAsyncThunk(
  "member/appointments/reschedule",
  async ({ appointmentId, timeSlotRequest }, { rejectWithValue }) => {
    try {
      const requestBody = {
        timeSlotRequest: {
          startTime: timeSlotRequest.startTime,
          endTime: timeSlotRequest.endTime,
          appointmentDate: timeSlotRequest.appointmentDate,
        },
        id: appointmentId,
        status: "SCHEDULED",
      };

      const response = await api.patch(
        `/appointment/${appointmentId}`,
        requestBody
      );

      if (response.data?.response_message === "Success") {
        return {
          status: "success",
          message: response.data,
          appointmentId,
        };
      } else {
        return rejectWithValue(
          response.data?.message?.payload || "Failed to reschedule appointment"
        );
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message?.payload || "Failed to reschedule appointment"
      );
    }
  }
);

/**
 * Submit appointment rating/review
 */
export const submitAppointmentRating = createAsyncThunk(
  "member/appointments/rating",
  async (ratingData, { rejectWithValue }) => {
    try {
      const response = await api.post("/ratings", ratingData);

      if (response.data?.response_message === "Success") {
        return {
          status: "success",
          message: response.data,
        };
      } else {
        return rejectWithValue(
          response.data?.message?.payload || "Failed to submit rating"
        );
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message?.payload || "Failed to submit rating"
      );
    }
  }
);

// Initial state
const initialState = {
  appointments: [],
  loading: false,
  error: null,
  cancelLoading: false,
  cancelError: null,
  rescheduleLoading: false,
  rescheduleError: null,
  slotsLoading: false,
  availableSlots: [],
  slotsError: null,
  ratingLoading: false,
  ratingError: null,
};

// Appointments slice
const memberAppointmentsSlice = createSlice({
  name: "memberAppointments",
  initialState,
  reducers: {
    clearAppointments: (state) => {
      state.appointments = [];
      state.error = null;
    },
    clearCancelError: (state) => {
      state.cancelError = null;
    },
    clearRescheduleError: (state) => {
      state.rescheduleError = null;
    },
    clearSlotsError: (state) => {
      state.slotsError = null;
    },
    clearRatingError: (state) => {
      state.ratingError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch appointments
    builder
      .addCase(getMemberAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMemberAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments = action.payload?.message?.payload?.data || [];
      })
      .addCase(getMemberAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Cancel appointment
    builder
      .addCase(cancelAppointment.pending, (state) => {
        state.cancelLoading = true;
        state.cancelError = null;
      })
      .addCase(cancelAppointment.fulfilled, (state, action) => {
        state.cancelLoading = false;
        // Update the appointment status in the local state
        const appointmentId = action.payload.appointmentId;
        state.appointments = state.appointments.map((apt) =>
          apt.appointmentId === appointmentId
            ? { ...apt, status: "CANCELED" }
            : apt
        );
      })
      .addCase(cancelAppointment.rejected, (state, action) => {
        state.cancelLoading = false;
        state.cancelError = action.payload;
      });

    // Reschedule appointment
    builder
      .addCase(rescheduleAppointment.pending, (state) => {
        state.rescheduleLoading = true;
        state.rescheduleError = null;
      })
      .addCase(rescheduleAppointment.fulfilled, (state, action) => {
        state.rescheduleLoading = false;
        // Appointment will be refreshed after reschedule
      })
      .addCase(rescheduleAppointment.rejected, (state, action) => {
        state.rescheduleLoading = false;
        state.rescheduleError = action.payload;
      });

    // Fetch available slots
    builder
      .addCase(getAvailableSlots.pending, (state) => {
        state.slotsLoading = true;
        state.slotsError = null;
      })
      .addCase(getAvailableSlots.fulfilled, (state, action) => {
        state.slotsLoading = false;
        state.availableSlots = action.payload?.message?.payload?.data || [];
      })
      .addCase(getAvailableSlots.rejected, (state, action) => {
        state.slotsLoading = false;
        state.slotsError = action.payload;
      });

    // Submit rating
    builder
      .addCase(submitAppointmentRating.pending, (state) => {
        state.ratingLoading = true;
        state.ratingError = null;
      })
      .addCase(submitAppointmentRating.fulfilled, (state) => {
        state.ratingLoading = false;
      })
      .addCase(submitAppointmentRating.rejected, (state, action) => {
        state.ratingLoading = false;
        state.ratingError = action.payload;
      });
  },
});

export const {
  clearAppointments,
  clearCancelError,
  clearRescheduleError,
  clearSlotsError,
  clearRatingError,
} = memberAppointmentsSlice.actions;

export default memberAppointmentsSlice.reducer;
